import { useMemo, useContext } from 'react';
import { paths } from 'src/routes/paths';
import SvgColor from 'src/components/svg-color';
import { NavProps } from 'src/components/nav-section';
import { AuthContext } from 'src/auth/context/jwt/auth-context';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SchoolIcon from '@mui/icons-material/School';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useOrganizationContext } from 'src/hooks/use-organization-context';
import { Role } from 'src/models/user';

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  eCommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  checklists: <ChecklistIcon sx={{ width: 1, height: 1 }} />,
  learningCenter: <SchoolIcon sx={{ width: 1, height: 1 }} />,
  myDashboard: icon('ic_my_dashboard'),
  orgDashboard: icon('ic_org_dashboard'),
  pages: icon('ic_pages'),
  reports: icon('ic_reports'),
  settings: icon('ic_settings'),
  jobRoles: <WorkOutlineIcon sx={{ width: 1, height: 1 }} />,
  skill: icon('ic_skill'),
};

export function useNavData(): NavProps['data'] {
  const { user } = useContext(AuthContext);
  const { currentOrg } = useOrganizationContext();

  const data = useMemo<NavProps['data']>(() => {
    if (!user) return [];

    const isSuperAdmin = user.role === Role.SUPER_ADMIN;
    const isAdmin = user.role === Role.ADMIN;
    const isManager = user.role === Role.MANAGER;

    // ── Super admin (not impersonating) sees ONLY platform-level navigation ──
    if (isSuperAdmin && !currentOrg) {
      return [
        {
          subheader: 'admin',
          items: [
            { title: 'Organizations', path: paths.admin.root, icon: ICONS.dashboard },
            { title: 'Super Admins', path: paths.admin.superAdmins, icon: ICONS.user },
          ],
        },
        {
          subheader: 'account',
          items: [
            { title: 'Profile', path: paths.user.profile, icon: ICONS.user, deep: false },
          ],
        },
      ];
    }

    // ── Super admin impersonating an org → falls through to org nav below ──
    // Treat them as admin for nav purposes; the ImpersonationBanner makes
    // the elevated context obvious.
    const effectiveAdmin = isAdmin || (isSuperAdmin && !!currentOrg);
    const effectiveManager = isManager;

    // ── Org users (admin / manager / staff) keep full org navigation ─────
    if (!currentOrg) return [];

    // Super admins impersonating an org don't have personal evaluations
    // there, so "My Dashboard" and "My Skills" are meaningless for them.
    const showPersonalItems = !(isSuperAdmin && !!currentOrg);

    return [
      {
        subheader: 'dashboards',
        items: [
          ...(showPersonalItems
            ? [{ title: 'My Dashboard', path: paths.dashboard.root, icon: ICONS.myDashboard }]
            : []),
          ...((effectiveAdmin || effectiveManager)
            ? [{
                title: 'Organization Dashboard',
                path: paths.organizationDashboard.root,
                icon: ICONS.orgDashboard,
              }]
            : []),
          { title: 'Articles', path: paths.articles.root, icon: ICONS.blog },
          ...(showPersonalItems
            ? [{ title: 'My Skills', path: paths.skills.root, icon: ICONS.skill }]
            : []),
        ],
      },

      ...((effectiveAdmin || effectiveManager)
        ? [{
            subheader: 'management',
            items: [
              { title: 'Users', path: paths.user.root, icon: ICONS.user, deep: false },
              { title: 'Job Roles', path: paths.admin.jobRoles, icon: ICONS.jobRoles },
              { title: 'Skill Builder', path: paths.skillBuilder.root, icon: ICONS.job },
              { title: 'Analytics', path: paths.analytics.root, icon: ICONS.analytics },
            ],
          }]
        : []),

      // Account section is personal — skip it for impersonating super admins.
      ...(showPersonalItems
        ? [{
            subheader: 'account',
            items: [
              { title: 'Profile', path: paths.user.profile, icon: ICONS.user, deep: false },
              { title: 'Notifications', path: paths.notifications.root, icon: ICONS.mail },
            ],
          }]
        : []),
    ];
  }, [currentOrg, user]);

  return data;
}
